import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import Footer from '../FlexiOssLanding/Footer';

const fade = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

// Editorial sub-heading inside a clause body
export const LegalSub = ({ children }) => (
  <h3 className="mt-7 mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#E87A20]">{children}</h3>
);

// Left-accented callout for warnings / emphasis
export const LegalCallout = ({ children, tone = 'accent' }) => {
  const map = {
    accent: { c: '#E87A20', b: 'rgba(232,122,32,0.4)', bg: 'rgba(232,122,32,0.08)' },
    warn: { c: '#fbbf24', b: 'rgba(251,191,36,0.4)', bg: 'rgba(251,191,36,0.08)' },
    danger: { c: '#f87171', b: 'rgba(248,113,113,0.4)', bg: 'rgba(248,113,113,0.08)' },
  };
  const s = map[tone] || map.accent;
  return (
    <div className="rounded-r-md border-l-2 py-3 pl-4 pr-4 text-[15px] font-semibold leading-relaxed" style={{ borderColor: s.c, backgroundColor: s.bg, color: s.c }}>
      {children}
    </div>
  );
};

// Reusable contact block (FMSi defaults)
export const LegalContact = ({
  intro = 'For questions, please contact:',
  name = 'Fortschritt Medical Supplies Inc.',
  address = 'Unit G11, Emerald Ave., Ortigas Center, Pasig City 1605',
  email = 'fms.medequip@gmail.com',
  phone = '(02) 8478 8009',
  mobile,
}) => {
  const { theme } = useTheme();
  const dark = theme !== 'light';
  return (
    <>
      <p>{intro}</p>
      <div className={`mt-3 rounded-xl border p-5 ${dark ? 'border-white/10 bg-white/[0.04]' : 'border-slate-200 bg-slate-50'}`}>
        <p className="font-bold">{name}</p>
        {address && <p className="mt-1">{address}</p>}
        {email && <p className="mt-1">Email: <a href={`mailto:${email}`}>{email}</a></p>}
        {phone && <p className="mt-1">Phone: {phone}</p>}
        {mobile && <p className="mt-1">Mobile: {mobile}</p>}
      </div>
    </>
  );
};

const LegalPageLayout = ({ testId, title, subtitle, updated, sections = [], accent = '#E87A20' }) => {
  const { theme } = useTheme();
  const dark = theme !== 'light';

  const bodyCls = [
    'space-y-4 text-[15px] leading-relaxed',
    dark ? 'text-slate-300' : 'text-slate-600',
    dark ? '[&_strong]:text-white' : '[&_strong]:text-slate-900',
    '[&_strong]:font-semibold',
    '[&_a]:font-semibold [&_a]:text-blue-500 [&_a:hover]:text-blue-400',
  ].join(' ');

  const num = (i) => String(i + 1).padStart(2, '0');

  return (
    <div
      data-testid={testId}
      className={`relative min-h-screen ${dark ? 'text-slate-100' : 'text-slate-900'}`}
      style={dark ? { background: 'linear-gradient(180deg,#001a33 0%,#00060f 65%)' } : { background: '#f8fafc' }}
    >
      {dark && (
        <>
          <div className="pointer-events-none fixed -top-24 right-0 h-80 w-80 rounded-full bg-[#E87A20]/8 blur-[130px]" />
          <div className="pointer-events-none fixed bottom-0 left-1/4 h-72 w-72 rounded-full bg-blue-600/8 blur-[130px]" />
        </>
      )}

      {/* Header */}
      <header className={`sticky top-0 z-30 border-b backdrop-blur-xl ${dark ? 'border-white/10 bg-[#001a33]/70' : 'border-slate-200 bg-white/80'}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-400 transition-colors hover:text-blue-300">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
          <img src="/flexioss-logo.png" alt="FlexiOss®" className={`h-9 ${dark ? '' : 'opacity-90'}`} />
        </div>
      </header>

      <main className="relative mx-auto max-w-7xl px-6 py-14 lg:px-8 lg:py-20">
        {/* Hero — left aligned editorial */}
        <motion.div variants={fade} initial="hidden" animate="show" className="mb-14 max-w-3xl border-l-2 border-[#E87A20] pl-6">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">Legal · FlexiOss® Philippines</p>
          <h1 className="text-4xl font-light tracking-tighter sm:text-5xl">{title}</h1>
          {subtitle && <p className="mt-4 text-base leading-relaxed text-slate-400 md:text-lg">{subtitle}</p>}
          {updated && <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Last updated: {updated}</p>}
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Sticky TOC */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Contents</p>
              <nav className="flex flex-col">
                {sections.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    data-testid={`toc-${s.id}`}
                    className={`group flex items-baseline gap-3 border-b py-2.5 text-sm transition-colors ${dark ? 'border-white/8 text-slate-400 hover:text-white' : 'border-slate-200 text-slate-500 hover:text-slate-900'}`}
                  >
                    <span className="text-[11px] font-bold tabular-nums text-[#E87A20]/80">{num(i)}</span>
                    <span className="transition-transform group-hover:translate-x-0.5">{s.title}</span>
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Clause content */}
          <div className="lg:col-span-8">
            {sections.map((s, i) => (
              <motion.section
                key={s.id}
                id={s.id}
                variants={fade}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
                className={`scroll-mt-28 border-t py-9 first:border-t-0 first:pt-0 ${dark ? 'border-white/10' : 'border-slate-200'}`}
              >
                <div className="flex gap-4 sm:gap-6">
                  <span
                    className="select-none text-3xl font-black leading-none tracking-tighter sm:text-4xl"
                    style={{ WebkitTextStroke: `1px ${accent}`, color: 'transparent' }}
                    aria-hidden="true"
                  >
                    {num(i)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-medium tracking-tight sm:text-2xl">{s.title}</h2>
                    <div className={`mt-4 ${bodyCls}`}>{s.body}</div>
                  </div>
                </div>
              </motion.section>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPageLayout;
